import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ProductSearchField } from "./loss/ProductSearchField";
import { ProductSelector } from "./loss/ProductSelector";
import { QuantityInput } from "./loss/QuantityInput";
import { useProductSearch } from "@/hooks/useProductSearch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface LossDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lossQuantity: string;
  onQuantityChange: (value: string) => void;
  onConfirm: (quantity: number, selectedProductId: string, explanation: string, proofUrl?: string) => void;
  stockItems: any[];
}

export const LossDialog = ({
  open,
  onOpenChange,
  lossQuantity,
  onQuantityChange,
  onConfirm,
  stockItems = [],
}: LossDialogProps) => {
  const {
    searchQuery,
    setSearchQuery,
    selectedProductId,
    setSelectedProductId,
    filteredProducts,
  } = useProductSearch(stockItems);

  const [explanation, setExplanation] = useState("");
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async () => {
    const quantity = parseInt(lossQuantity);
    
    if (!selectedProductId) {
      toast.error("Veuillez sélectionner un produit");
      return;
    }

    if (!quantity || quantity <= 0) {
      toast.error("Veuillez entrer une quantité valide");
      return;
    }

    if (!explanation.trim()) {
      toast.error("Veuillez expliquer la raison de la perte");
      return;
    }

    try {
      setIsUploading(true);
      let proofUrl = null;

      if (proofFile) {
        const fileExt = proofFile.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('loss_proofs')
          .upload(filePath, proofFile);

        if (uploadError) throw uploadError;

        proofUrl = filePath;
      }

      onConfirm(quantity, selectedProductId, explanation, proofUrl);
      setExplanation("");
      setProofFile(null);
    } catch (error: any) {
      console.error("Erreur lors de l'upload:", error);
      toast.error("Erreur lors de l'upload du fichier");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error("Le fichier est trop volumineux (max 5MB)");
        return;
      }
      setProofFile(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Déclarer une perte</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <ProductSearchField 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />

          <ProductSelector
            selectedProductId={selectedProductId}
            setSelectedProductId={setSelectedProductId}
            filteredProducts={filteredProducts}
          />

          <div className="space-y-2">
            <QuantityInput
              lossQuantity={lossQuantity}
              onQuantityChange={onQuantityChange}
              label=""
              placeholder="Entrez la quantité perdue"
            />
          </div>

          <div className="space-y-2">
            <Label>Raison de la perte</Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Expliquez la raison de la perte..."
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Preuve de perte (optionnel)</Label>
            <Input
              type="file"
              onChange={handleFileChange}
              accept="image/*,video/*"
              className="cursor-pointer"
            />
            <p className="text-sm text-muted-foreground">
              Formats acceptés: images et vidéos (max 5MB)
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? "Envoi en cours..." : "Confirmer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};